import { type ApolloClient, gql } from '@apollo/client/core';
import { Query } from '../../generated/graphql';
import * as Artist from './Artist';
import * as SearchResultArtist from './SearchResultArtist';
import * as EventArtist from './EventArtist';
import * as EventArtistRider from './EventArtistRider';
import { VPResult } from '../../api/VPResult';

export const ARTIST_FIELDS = gql`
  fragment ArtistFields on Artist {
    id
    name
    bio
    links {
      id
      url
    }
    attachments {
      attachment {
        id
        name
        status
        url
      }
    }
  }
`;

export const ARTIST_SEARCH_QUERY = gql`
  query ArtistSearch($search: String) {
    app {
      artists(search: $search) {
        id
        name
        imageUrl
        soundchartsUuid
      }
    }
  }
`;

export const ARTIST_QUERY = gql`
  query Artist($id: Int!) {
    app {
      artist(id: $id) {
        ...ArtistFields
      }
    }
  }
  ${ARTIST_FIELDS}
`;

export const searchArtists = async (client: ApolloClient<Query>, query: string) => {
  const { data } = await client.query({
    query: ARTIST_SEARCH_QUERY,
    variables: {
      search: query,
    },
  });

  return SearchResultArtist.fromGqlCollection(data.app.artists);
};

export const fetchArtist = async (client: ApolloClient<Query>, id: number) => {
  const { data } = await client.query({
    query: ARTIST_QUERY,
    variables: {
      id,
    },
  });

  return Artist.fromGql(data.app.artist);
};

export const saveArtist = async (client: ApolloClient<Query>, artist: Artist.Artist) => {
  const mutation = gql`
    mutation SaveArtist($artist: ArtistInput!) {
      app {
        saveArtist(input: { artist: $artist }) {
          artist {
            ...ArtistFields
          }
          errors
        }
      }
    }
    ${ARTIST_FIELDS}
  `;

  await client.mutate({
    mutation,
    variables: {
      artist,
    },
  });
};

const ADD_EVENT_ARTISTS_MUTATION = gql`
  mutation AddEventArtist($eventId: Int!, $artist: ArtistInput!) {
    app {
      addEventArtist(input: { eventId: $eventId, artist: $artist}) {
        eventArtist {
          id
          artist {
            ...ArtistFields
          }
        }
        clientMutationId
        errors
        success
      }
    }
  }
  ${ARTIST_FIELDS}
`;

type AddEventArtistParams = {
  eventId: number;
  artist: {
    id?: number;
    name?: string;
    platformUrl?: string;
  };
};

export const addEventArtist = async (client: ApolloClient<Query>, addParams: AddEventArtistParams): Promise<VPResult<EventArtist.EventArtist>> => {
  const { data } = await client.mutate({
    mutation: ADD_EVENT_ARTISTS_MUTATION,
    variables: {
      ...addParams,
    },
    update: cache => {
      cache.evict({
        id: 'ROOT_QUERY',
        // Probably needs better cache clearing
        fieldName: 'app',
      });
      cache.gc();
    },
  });

  const response = data.app.addEventArtist;
  if (response.success) {
    return {
      success: true,
      data: EventArtist.fromGql(response.eventArtist),
    };
  }

  return {
    success: false,
    errors: response.errors,
  };
};

const REMOVE_EVENT_ARTIST_MUTATION = gql`
  mutation RemoveEventArtist($eventArtistId: Int!) {
    app {
      removeEventArtist(input: { eventArtistId: $eventArtistId}) {
        clientMutationId
        errors
        success
      }
    }
  }
`;

type RemoveEventArtistParams = {
  eventArtistId: number;
};

export const removeEventArtist = async (client: ApolloClient<Query>, removeParams: RemoveEventArtistParams) => {
  const { data } = await client.mutate({
    mutation: REMOVE_EVENT_ARTIST_MUTATION,
    variables: {
      ...removeParams,
    },
    update: cache => {
      cache.evict({
        id: 'ROOT_QUERY',
        // Probably needs better cache clearing
        fieldName: 'app',
      });
      cache.gc();
    },
  });

  const response = data.app.removeEventArtist;
  if (response.success) {
    return {
      success: true,
    };
  }

  return {
    success: false,
    errors: response.errors,
  };
};

const REORDER_EVENT_ARTISTS_MUTATION = gql`
  mutation ReorderEventArtists($eventId: Int!, $eventArtistIds: [Int!]!) {
    app {
      reorderEventArtists(input: { eventId: $eventId, eventArtistIds: $eventArtistIds }) {
        success
        eventArtists {
          id
          artist {
            ...ArtistFields
          }
        }
      }
    }
  }
  ${ARTIST_FIELDS}
`;

type ReorderEventArtistsParams = {
  eventId: number;
  eventArtistIds: number[];
};

export const reorderEventArtists = async (client: ApolloClient<Query>, reorderParams: ReorderEventArtistsParams): Promise<VPResult<EventArtist.EventArtist[]>> => {
  const { data } = await client.mutate({
    mutation: REORDER_EVENT_ARTISTS_MUTATION,
    variables: {
      ...reorderParams,
    },
    update: cache => {
      cache.evict({
        id: 'ROOT_QUERY',
        // Probably needs better cache clearing
        fieldName: 'app',
      });
      cache.gc();
    },
  });

  const response = data.app.reorderEventArtists;
  if (response.success) {
    return {
      success: true,
      data: EventArtist.fromGqlCollection(response.eventArtists),
    };
  }

  return {
    success: false,
  };
};

const EVENT_ARTIST_RIDERS_QUERY = gql`
  query GetEventArtistRiders($eventId: Int!, $artistId: Int) {
    event(id: $eventId) {
      eventArtists(artistId: $artistId) {
        id
        artist {
          id
          name
        }
        riders {
          id
          notes
          riderType
          artistLoadInTime
          stageSoundCheckTime
          soundCheckType
          stagePerformersNumber
          backstagePassesNumber
          attachments {
            id
            name
            mime
            url
            status
          }
        }
      }
    }
  }
`;

type EventArtistRidersParams = {
  eventId: number;
  eventArtistId?: number;
};

export const fetchEventArtistRiders = async (client: ApolloClient<Query>, params: EventArtistRidersParams): Promise<Pick<EventArtist.EventArtist, 'id' | 'artist' | 'riders'>[]> => {
  const { data } = await client.query({
    query: EVENT_ARTIST_RIDERS_QUERY,
    variables: {
      ...params,
    },
    fetchPolicy: 'network-only',
  });

  return EventArtist.fromGqlCollection(data.event.eventArtists);
};

const SAVE_EVENT_ARTIST_RIDER_MUTATION = gql`
  mutation SaveEventArtistRider($eventId: Int!, $artistId: Int!, $rider: EventArtistRiderInput!) {
    app {
      saveEventArtistRider(input: { eventId: $eventId, artistId: $artistId , rider: $rider }) {
        rider {
          id
          notes
          riderType
          artistLoadInTime
          stageSoundCheckTime
          soundCheckType
          stagePerformersNumber
          backstagePassesNumber
          attachments {
            id
            name
            mime
            url
            status
          }
        }
        errors
        success
      }
    }
  }
`;

type EventArtistRiderInput = {
  id?: number;
  notes?: string;
  riderType: EventArtistRider.EventArtistRiderType;
  attachments?: {
    id: number;
    _destroy?: boolean;
  }[];
};

export const saveEventArtistRider = async (client: ApolloClient<Query>, { eventId, artistId, rider }: { eventId: number; artistId: number; rider: EventArtistRiderInput }): Promise<VPResult<EventArtistRider.EventArtistRider>> => {
  const { data } = await client.mutate({
    mutation: SAVE_EVENT_ARTIST_RIDER_MUTATION,
    variables: {
      eventId,
      artistId,
      rider,
    },
    update: cache => {
      cache.evict({
        id: 'ROOT_QUERY',
        // Probably needs better cache clearing
        fieldName: 'app',
      });
      cache.gc();
    },
  });

  const response = data.app.saveEventArtistRider;
  if (response.success) {
    return {
      success: true,
      data: EventArtistRider.fromGql(response.rider),
    };
  }

  return {
    success: false,
    errors: response.errors,
  };
};
